/**
 * Frontend API client that calls the backend API
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api'

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL
    this.cache = new Map()
    this.pendingRequests = new Map()
  }

  async request(endpoint, options = {}, isRetry = false) {
    const { cache: cacheOptions, ...fetchOptions } = options
    const url = `${this.baseUrl}${endpoint}`
    const method = (fetchOptions.method || 'GET').toUpperCase()
    const cacheTtl = method === 'GET' && cacheOptions?.ttl > 0 ? cacheOptions.ttl : 0
    const cacheKey = `${method}:${url}`

    if (cacheTtl > 0 && !isRetry) {
      const cached = this.cache.get(cacheKey)
      if (cached && cached.expiresAt > Date.now()) {
        return cached.data
      }

      if (cached) {
        this.cache.delete(cacheKey)
      }

      const pendingRequest = this.pendingRequests.get(cacheKey)
      if (pendingRequest) {
        return pendingRequest
      }
    }

    // Get auth token for Azure Functions
    const token = this.getAuthToken()

    const config = {
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Origin': window.location.origin,
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...fetchOptions.headers
      },
      ...fetchOptions
    }

    const requestPromise = (async () => {
      try {
        const response = await fetch(url, config)

        if (!response.ok) {
          // Handle 401 Unauthorized - Token expired
          if (response.status === 401 && !isRetry) {
            console.log('Token expired, attempting to refresh...')
            try {
              await this.refreshToken()
              console.log('Token refreshed successfully, retrying request...')
              // Retry the request once with the new token
              return this.request(endpoint, options, true)
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError)
              throw new Error('Session expired. Please refresh the page.')
            }
          }

          let errorMessage = `HTTP error! status: ${response.status}`

          // Try to extract the actual error message from the response body
          try {
            const errorData = await response.json()
            if (errorData && errorData.error) {
              errorMessage = errorData.error
            }
          } catch (parseError) {
            // If we can't parse the response as JSON, use the status text
            errorMessage = response.statusText || errorMessage
          }

          throw new Error(errorMessage)
        }

        // Handle different success responses
        if (response.status === 204) {
          // 204 No Content - successful operation with no response body
          if (method !== 'GET') {
            this.clearCache()
          }
          return { success: true, data: null }
        }

        // For other success responses, parse JSON
        const responseText = await response.text()
        if (!responseText) {
          // Empty response body but successful status
          if (method !== 'GET') {
            this.clearCache()
          }
          return { success: true, data: null }
        }

        const result = JSON.parse(responseText)

        if (method !== 'GET') {
          // Mutations can affect any previously cached read.
          this.clearCache()
        } else if (cacheTtl > 0) {
          this.cache.set(cacheKey, {
            data: result,
            expiresAt: Date.now() + cacheTtl
          })
        }

        return result
      } catch (error) {
        console.error(`API request failed: ${endpoint}`, error)
        throw error
      }
    })()

    if (cacheTtl > 0 && !isRetry) {
      this.pendingRequests.set(cacheKey, requestPromise)
      requestPromise.then(() => {
        if (this.pendingRequests.get(cacheKey) === requestPromise) {
          this.pendingRequests.delete(cacheKey)
        }
      }, () => {
        if (this.pendingRequests.get(cacheKey) === requestPromise) {
          this.pendingRequests.delete(cacheKey)
        }
      })
    }

    return requestPromise
  }

  clearCache() {
    this.cache.clear()
    this.pendingRequests.clear()
  }

  async refreshToken() {
    // Import AuthService dynamically to avoid circular imports
    const { authService } = await import('./AuthService.js')
    return authService.refreshToken()
  }

  getAuthToken() {
    // Try localStorage first, then cookies
    return localStorage.getItem('authToken') || this.getCookie('auth_token')
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  // Customer methods
  async getCustomers(limit = null, search = null) {
    const params = new URLSearchParams()
    if (limit) params.append('limit', limit)
    if (search) params.append('search', search)
    
    const queryString = params.toString() ? `?${params.toString()}` : ''
    console.log(`ApiService.getCustomers called with limit: ${limit}, search: ${search}, URL: /customers${queryString}`)
    return this.request(`/customers${queryString}`, { cache: { ttl: 5 * 60 * 1000 } })
  }

  async getContactsByCustomer(customerId) {
    return this.request(`/customers/${customerId}/contacts`, { cache: { ttl: 5 * 60 * 1000 } })
  }

  // Product methods
  async getProducts(searchTerm = '', limit = null) {
    const params = new URLSearchParams()
    if (searchTerm) params.append('search', searchTerm)
    if (limit) params.append('limit', limit.toString())

    const queryString = params.toString()
    return this.request(`/products${queryString ? `?${queryString}` : ''}`, { cache: { ttl: 5 * 60 * 1000 } })
  }

  async getProduct(productId) {
    return this.request(`/products/${productId}`, { cache: { ttl: 5 * 60 * 1000 } })
  }

  async getProductAttributes(productNumber, limit = null) {
    const params = new URLSearchParams()
    if (limit) params.append('limit', limit.toString())

    const queryString = params.toString()
    const encodedProductNumber = encodeURIComponent(productNumber)
    return this.request(`/products/${encodedProductNumber}/attributes${queryString ? `?${queryString}` : ''}`, { cache: { ttl: 5 * 60 * 1000 } })
  }

  async getProductOrderSettings(productNumber) {
    const encodedProductNumber = encodeURIComponent(productNumber)
    return this.request(`/products/${encodedProductNumber}/order-settings`, { cache: { ttl: 5 * 60 * 1000 } })
  }

  // Design methods
  async getDesigns(searchTerm = '', limit = 100) {
    const params = new URLSearchParams()
    if (searchTerm) params.append('search', searchTerm)
    if (limit) params.append('limit', limit.toString())

    const queryString = params.toString()
    return this.request(`/designs${queryString ? `?${queryString}` : ''}`, { cache: { ttl: 5 * 60 * 1000 } })
  }

  // Prospect methods
  async getProspects(search = null) {
    const queryString = search ? `?search=${encodeURIComponent(search)}` : ''
    return this.request(`/prospects${queryString}`, { cache: { ttl: 5 * 60 * 1000 } })
  }

  // Opportunity methods
  async getOpportunities() {
    return this.request('/opportunities', { cache: { ttl: 5 * 60 * 1000 } })
  }

  // Quotation methods
  async getQuotationsCount() {
    return this.request('/quotations-count', { cache: { ttl: 30 * 1000 } })
  }

  async getOpenQuotationsCount() {
    return this.request('/quotations/open/count', { cache: { ttl: 30 * 1000 } })
  }

  async getFollowupThisWeekCount() {
    return this.request('/quotations/followup/week/count', { cache: { ttl: 30 * 1000 } })
  }

  async getExpiryThisWeekCount() {
    return this.request('/quotations/expiry/week/count', { cache: { ttl: 30 * 1000 } })
  }

  async getFollowupPastDueCount() {
    return this.request('/quotations/followup/pastdue/count', { cache: { ttl: 30 * 1000 } })
  }

  async getExpiryPastDueCount() {
    return this.request('/quotations/expiry/pastdue/count', { cache: { ttl: 30 * 1000 } })
  }

  async getQuotations() {
    return this.request('/quotations')
  }

  async getQuotationsPaginated(page = 1, pageSize = 20, filter = '', orderBy = '', creatorEmail = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      filter,
      orderBy,
      creatorEmail
    })

    return this.request(`/quotationspaginated?${params}`)
  }

  async getMyQuotationsPaginated(page = 1, pageSize = 20, filter = '', orderBy = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      filter,
      orderBy
    })

    return this.request(`/my-quotationspaginated?${params}`)
  }

  async getQuotationByNumber(quotationNumber) {
    return this.request(`/quotations/${quotationNumber}`, { cache: { ttl: 30 * 1000 } })
  }

  async createQuotation(quotationData) {
    return this.request('/quotations', {
      method: 'POST',
      body: JSON.stringify(quotationData)
    })
  }

  async updateQuotation(quotationId, updates) {
    return this.request(`/quotations/${quotationId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    })
  }

  async addQuotationLine(quotationId, lineData) {
    return this.request(`/quotations/${quotationId}/lines`, {
      method: 'POST',
      body: JSON.stringify(lineData)
    })
  }

  async getQuotationLines(quotationNumber) {
    return this.request(`/quotations/${quotationNumber}/lines`, { cache: { ttl: 30 * 1000 } })
  }

  async updateQuotationLines(quotationNumber, lines) {
    return this.request(`/quotations/${quotationNumber}/lines`, {
      method: 'PUT',
      body: JSON.stringify({ lines })
    })
  }

  async deleteQuotationLine(lineId) {
    // Use the entity ID directly in the URL path for Dataverse deletion
    return this.request(`/quotations/lines/${lineId}`, {
      method: 'DELETE'
    })
  }

  async deleteQuotation(quotationNumber) {
    return this.request(`/quotations/${quotationNumber}`, {
      method: 'DELETE'
    })
  }

  async updateQuotationHeader(quotationNumber, headerData) {
    return this.request(`/quotations/${quotationNumber}/header`, {
      method: 'PUT',
      body: JSON.stringify(headerData)
    })
  }

  async getContactPersons() {
    // The backend should filter by the logged user's company automatically
    // based on authentication context, same as other entity endpoints
    return this.request('/contacts', { cache: { ttl: 5 * 60 * 1000 } })
  }

  async getContactPerson(contactPersonId) {
    // The backend should filter by the logged user's company automatically
    // based on authentication context, same as other entity endpoints
    return this.request(`/contacts/${contactPersonId}`, { cache: { ttl: 5 * 60 * 1000 } })
  }

  async getSmmParameters({ refresh = false } = {}) {
    // Get SMM parameters filtered by user's company
    // Backend automatically filters by authenticated user's company (dataareaid)
    const query = refresh ? '?refresh=true' : ''
    if (refresh) {
      this.clearCache()
    }
    return this.request(`/smmparameters${query}`, refresh ? {} : { cache: { ttl: 10 * 60 * 1000 } })
  }

  async getUserLanguage() {
    return this.request('/user/language', { cache: { ttl: 10 * 60 * 1000 } })
  }

  async getSalutations() {
    return this.request('/salutations')
  }
}

export const apiService = new ApiService()
